// src/shared/api/interceptors/index.ts
//
// Interceptors are registered by CALLING registerInterceptors(), not by
// importing this module for its side effects.
//
// The previous shape was `import './auth'; import './namespace'; …` — bare
// side-effect imports with nothing consumed from them. That works only while
// the bundler assumes every module is impure. The moment package.json declared
// `sideEffects`, Rollup dropped all three from the production build: auth
// headers, namespace rewriting and the 401-refresh retry silently disappeared,
// and only in built output (the dev server never tree-shakes). Calling exported
// functions keeps them reachable through real bindings, so tree-shaking stays
// safe and `sideEffects` can be declared without this trap re-appearing.

import { registerAuthInterceptor } from './auth';
import { registerNamespaceInterceptor } from './namespace';
import { registerRetryInterceptor } from './retry';
import { registerCircuitBreakerInterceptor } from './circuitBreaker';

export { setAuth0TokenGetter } from './auth';
export {
  CIRCUIT_OPEN_CODE,
  resetCircuitBreakers,
  isCircuitOpenFor,
} from './circuitBreaker';

let registered = false;

/**
 * Install every axios interceptor exactly once.
 *
 * Idempotent because fetcher / mutator / upload each call it at module load —
 * axios has no dedupe of its own, so a second registration would run the whole
 * chain twice per request.
 *
 * Order matters and mirrors the original import order: auth (attach token) →
 * namespace (rewrite URL, add x-store) on the request side, then retry on the
 * response side.
 *
 * The circuit breaker is registered LAST, which puts it FIRST on the request
 * path (axios unshifts request interceptors) and LAST on the response path.
 * Both are what we want: an open breaker rejects before any other interceptor
 * does work, and failures are recorded after the 401 retry has had its chance,
 * so a refreshed-and-replayed request is judged on its final outcome. It also
 * means the breaker sees the pre-rewrite URL outbound and the post-rewrite one
 * inbound — hence the namespace-stripping in its key.
 */
export function registerInterceptors(): void {
  if (registered) return;
  registered = true;

  registerAuthInterceptor();
  registerNamespaceInterceptor();
  registerRetryInterceptor();
  registerCircuitBreakerInterceptor();
}

/** Test-only: allow a suite to re-register against a fresh axios instance. */
export function __resetInterceptorRegistration(): void {
  registered = false;
}
